/*
 * VX Case — Leather Collection (template de coleção).
 * Portado da página do totem. Cada seção se inicializa sozinha e volta a
 * inicializar quando é recarregada no editor de temas.
 */
(() => {
  "use strict";

  /* Cada seção inclui este arquivo; só a primeira cópia executa. */
  if (window.lcLeatherCollection) return;
  window.lcLeatherCollection = true;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const hasObserver = "IntersectionObserver" in window;

  const onVisibilityChange = (callback) => document.addEventListener("visibilitychange", callback);

  /* Hero: carrossel automático com gesto horizontal ------------------------ */
  const initHero = (root) => {
    /* O hero ocupa a primeira tela descontando o cabeçalho e as barras da loja acima dele. */
    const measureOffset = () => {
      const top = root.getBoundingClientRect().top + window.scrollY;
      root.style.setProperty("--lc-hero-offset", `${Math.max(0, Math.round(top))}px`);
    };
    measureOffset();
    window.addEventListener("resize", measureOffset, { passive: true });

    const carousel = root.querySelector("[data-lc-hero-carousel]");
    const slides = Array.from(root.querySelectorAll(".lc-hero__slide"));
    if (!carousel || slides.length < 2) return;

    const desktopQuery = window.matchMedia("(min-width: 1081px)");
    const preparations = new Map();
    let active = 0;
    let timer;
    let startToken = 0;
    let isNear = false;
    let transition;
    let touchX = 0;
    let touchY = 0;

    const normalize = (index) => (index + slides.length) % slides.length;

    /* Os slides além do primeiro guardam as imagens em data-*, e só carregam perto de aparecer. */
    const prepare = (index) => {
      const slide = slides[normalize(index)];
      const image = slide?.querySelector("img");
      if (!image) return Promise.resolve(false);

      const existing = preparations.get(slide);
      if (existing) return existing;

      slide.querySelectorAll("source[data-srcset]").forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute("data-srcset");
      });

      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        image.removeAttribute("data-srcset");
      }

      if (image.dataset.src) {
        image.src = image.dataset.src;
        image.removeAttribute("data-src");
      }

      const decode = () =>
        typeof image.decode === "function" ? image.decode().then(() => true, () => true) : Promise.resolve(true);

      const preparation = image.complete && image.naturalWidth > 0
        ? decode()
        : new Promise((resolve) => {
            image.addEventListener("load", () => decode().then(resolve), { once: true });
            image.addEventListener("error", () => resolve(false), { once: true });
          });

      preparations.set(slide, preparation);
      return preparation;
    };

    const preloadNeighbors = (index) => {
      const run = () => {
        prepare(index + 1).finally(() => prepare(index - 1));
      };

      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(run, { timeout: 1500 });
      } else {
        window.setTimeout(run, 250);
      }
    };

    const apply = (index) => {
      active = normalize(index);

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === active;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", String(!isActive));
      });

      const tone = desktopQuery.matches ? slides[active].dataset.toneDesktop : slides[active].dataset.tone;
      root.classList.toggle("has-dark-logo", tone === "dark");
      preloadNeighbors(active);
    };

    const show = (index) => {
      const target = normalize(index);
      if (target === active) {
        apply(target);
        return Promise.resolve(true);
      }

      if (transition) return transition;

      transition = prepare(target)
        .then((ready) => {
          if (ready) apply(target);
          return ready;
        })
        .finally(() => {
          transition = undefined;
        });

      return transition;
    };

    const stop = () => {
      startToken += 1;
      window.clearTimeout(timer);
    };

    const canPlay = () => isNear && !document.hidden && !reducedMotion;

    const start = () => {
      stop();
      if (!canPlay()) return;

      const token = startToken;
      prepare(active).finally(() => {
        if (token !== startToken || !canPlay()) return;

        timer = window.setTimeout(() => {
          show(active + 1).finally(start);
        }, 5000);
      });
    };

    carousel.addEventListener(
      "touchstart",
      (event) => {
        touchX = event.changedTouches[0].clientX;
        touchY = event.changedTouches[0].clientY;
      },
      { passive: true },
    );

    carousel.addEventListener(
      "touchend",
      (event) => {
        const distanceX = event.changedTouches[0].clientX - touchX;
        const distanceY = event.changedTouches[0].clientY - touchY;
        if (Math.abs(distanceX) < 45 || Math.abs(distanceX) <= Math.abs(distanceY)) return;

        show(active + (distanceX < 0 ? 1 : -1)).finally(start);
      },
      { passive: true },
    );

    const setNear = (value) => {
      if (isNear === value) return;
      isNear = value;
      if (isNear) start();
      else stop();
    };

    if (hasObserver) {
      new IntersectionObserver(([entry]) => setNear(entry.isIntersecting), {
        rootMargin: "100% 0px 100% 0px",
      }).observe(root);
    } else {
      setNear(true);
    }

    onVisibilityChange(() => (document.hidden ? stop() : start()));
    desktopQuery.addEventListener?.("change", () => apply(active));

    apply(0);
  };

  /* Manifesto: órbita 3D das capas ----------------------------------------- */
  const initManifesto = (root) => {
    const orbit = root.querySelector("[data-lc-orbit]");
    const items = Array.from(root.querySelectorAll("[data-lc-orbit-item]"));
    if (!orbit || items.length < 2) return;

    const HOLD = 5000;
    const ROTATION = 950;
    let front = Math.max(0, items.findIndex((item) => item.classList.contains("is-front")));
    let holdTimer = 0;
    let transitionTimer = 0;
    let isVisible = false;

    const stop = () => {
      window.clearTimeout(holdTimer);
      window.clearTimeout(transitionTimer);
    };

    const applyPositions = () => {
      const left = (front - 1 + items.length) % items.length;

      items.forEach((item, index) => {
        item.classList.remove("is-left", "is-front", "is-right", "is-active");
        if (index === front) item.classList.add("is-front");
        else if (index === left) item.classList.add("is-left");
        else item.classList.add("is-right");
      });
    };

    const rotate = () => {
      if (!isVisible || document.hidden || reducedMotion) return;

      items.forEach((item) => item.classList.remove("is-active"));
      front = (front + 1) % items.length;
      applyPositions();
      transitionTimer = window.setTimeout(hold, ROTATION);
    };

    function hold() {
      if (!isVisible || document.hidden) return;

      items[front].classList.add("is-active");
      if (!reducedMotion) holdTimer = window.setTimeout(rotate, HOLD);
    }

    const setVisible = (value) => {
      if (isVisible === value) return;
      isVisible = value;
      stop();
      items.forEach((item) => item.classList.remove("is-active"));
      if (isVisible) hold();
    };

    items.forEach((item) => item.classList.remove("is-active"));

    if (hasObserver) {
      new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting)).observe(orbit);
    } else {
      setVisible(true);
    }

    onVisibilityChange(() => {
      stop();
      items.forEach((item) => item.classList.remove("is-active"));
      if (!document.hidden && isVisible) hold();
    });
  };

  /* Revela conteúdos quando entram na tela --------------------------------- */
  const initReveal = (root) => {
    const elements = root.querySelectorAll(".lc-reveal:not(.is-visible)");

    if (reducedMotion || !hasObserver) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8%", threshold: 0.12 },
    );

    elements.forEach((element) => observer.observe(element));
  };

  /* Reels: esteira infinita com autoplay e arraste ------------------------- */
  const initReels = (root) => {
    const track = root.querySelector("[data-lc-reels-track]");
    if (!track) return;

    const IDLE_DELAY = 1200;
    const PLAY_DELAY = 320;
    const MOVE_DURATION = reducedMotion ? 0 : 900;

    let dragging = false;
    let moved = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragStartScroll = 0;
    let dragAxis = null;
    let visible = false;
    let interacting = false;
    let setWidth = 0;
    let originals = [];
    let allCards = [];
    let videos = [];
    let visibleCards = new Set();
    const playing = new Set();
    const requested = new WeakSet();
    let cardObserver = null;
    let syncFrame = 0;
    let resizeFrame = 0;
    let firstOriginal = null;
    let firstTrailingClone = null;
    let lastOriginal = null;
    let lastLeadingClone = null;
    let activeCard = null;
    let activeVideo = null;
    let autoTimer = 0;
    let interactionTimer = 0;
    let animationFrame = 0;

    const clearTimers = () => {
      window.clearTimeout(autoTimer);
      window.clearTimeout(interactionTimer);
    };

    const stopAnimation = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      track.classList.remove("is-auto-scrolling");
    };

    const pauseAll = () => {
      videos.forEach((video) => video.pause());
      playing.clear();
      activeCard = null;
      activeVideo = null;
    };

    const centerScroll = (card) => (card ? card.offsetLeft + card.offsetWidth / 2 - track.clientWidth / 2 : 0);

    const getCentered = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      return allCards.reduce((closest, card) => {
        const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center);
        return !closest || distance < closest.distance ? { card, distance } : closest;
      }, null)?.card ?? null;
    };

    const visibleFromLayout = () => {
      const start = track.scrollLeft;
      const end = start + track.clientWidth;
      return allCards.filter((card) => card.offsetLeft + card.offsetWidth > start && card.offsetLeft < end);
    };

    const updateVisibleFromLayout = () => {
      visibleCards = new Set(visibleFromLayout());
    };

    const ensureReady = (video) => {
      if (!video || requested.has(video)) return;
      requested.add(video);
      video.preload = "auto";
      video.load();
    };

    const play = (video) => {
      if (!video) return;
      ensureReady(video);
      video.muted = true;
      video.playsInline = true;
      video.play()?.catch(() => {});
      playing.add(video);
    };

    const pause = (video) => {
      video.pause();
      playing.delete(video);
    };

    const setFocus = (focused = null) => {
      allCards.forEach((card) => card.classList.toggle("is-centered", card === focused));
    };

    const startVisible = () => {
      if (!visible || document.hidden) {
        pauseAll();
        return;
      }

      const visibleVideos = new Set();
      visibleCards.forEach((card) => {
        const video = card.querySelector("video");
        if (video) visibleVideos.add(video);
      });

      Array.from(playing).forEach((video) => {
        if (!visibleVideos.has(video)) pause(video);
      });

      visibleVideos.forEach((video) => {
        if (video !== activeVideo) video.loop = true;
        if (video.paused) play(video);
      });
    };

    const scheduleSync = () => {
      if (syncFrame) return;
      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = 0;
        if (!cardObserver) updateVisibleFromLayout();
        startVisible();
      });
    };

    const normalizeLoop = () => {
      if (!setWidth || !firstOriginal) return;

      const firstCenter = centerScroll(firstOriginal);
      const step = setWidth / originals.length;
      const lower = firstCenter - step / 2;
      const upper = firstCenter + setWidth - step / 2;
      let next = track.scrollLeft;

      while (next < lower) next += setWidth;
      while (next >= upper) next -= setWidth;

      if (Math.abs(next - track.scrollLeft) > 0.5) track.scrollLeft = next;
    };

    /* Iguala o frame da cópia antes de atravessar a emenda do loop. */
    const syncFrameTo = (sourceCard, targetCard) =>
      new Promise((resolve) => {
        const source = sourceCard?.querySelector("video");
        const target = targetCard?.querySelector("video");
        let settled = false;
        const settle = () => {
          if (settled) return;
          settled = true;
          resolve();
        };

        if (!source || !target || !Number.isFinite(source.currentTime)) {
          settle();
          return;
        }

        requested.add(target);

        const applyTime = () => {
          const duration = Number.isFinite(target.duration) ? target.duration : source.currentTime;
          const time = Math.max(0, Math.min(source.currentTime, Math.max(0, duration - 0.04)));

          if (Math.abs(target.currentTime - time) < 0.04 && target.readyState >= 2) {
            settle();
            return;
          }

          target.addEventListener("seeked", settle, { once: true });
          try {
            target.currentTime = time;
          } catch {
            settle();
            return;
          }
          window.setTimeout(settle, 180);
        };

        if (target.readyState > 0) {
          applyTime();
          return;
        }

        target.preload = "auto";
        target.addEventListener("loadedmetadata", applyTime, { once: true });
        target.load();
        window.setTimeout(settle, 260);
      });

    const syncVisibleForRebase = async () => {
      await Promise.all(
        visibleFromLayout().map((sourceCard) => {
          const targetLeft = sourceCard.offsetLeft - setWidth;
          const target = allCards.reduce((closest, card) => {
            if (card === sourceCard) return closest;
            const distance = Math.abs(card.offsetLeft - targetLeft);
            return !closest || distance < closest.distance ? { card, distance } : closest;
          }, null);
          return target?.distance < 2 ? syncFrameTo(sourceCard, target.card) : Promise.resolve();
        }),
      );
    };

    const restartOutgoing = (card) => {
      const video = card?.querySelector("video");
      if (!video) return;

      video.loop = true;
      if (video.ended || (Number.isFinite(video.duration) && video.currentTime >= video.duration - 0.05)) {
        try {
          video.currentTime = 0;
        } catch {
          /* O primeiro frame chega quando o vídeo estiver pronto. */
        }
      }
      play(video);
    };

    const activate = (card) => {
      if (!card || !visible || interacting || document.hidden) return;

      const video = card.querySelector("video");
      if (!video) return;

      if (activeVideo && activeVideo !== video) restartOutgoing(activeCard);

      activeCard = card;
      activeVideo = video;
      video.loop = false;
      try {
        video.currentTime = 0;
      } catch {
        /* Reproduz assim que os metadados chegarem. */
      }

      setFocus(card);
      play(video);
      startVisible();
    };

    const animateTo = (card, onComplete) => {
      if (!card) return;
      stopAnimation();

      const start = track.scrollLeft;
      const travel = centerScroll(card) - start;

      if (MOVE_DURATION === 0 || Math.abs(travel) < 1) {
        track.scrollLeft = start + travel;
        track.classList.remove("is-loop-rebasing");
        normalizeLoop();
        onComplete?.();
        return;
      }

      const startTime = performance.now();
      track.classList.add("is-auto-scrolling");
      track.classList.remove("is-loop-rebasing");

      const frame = (time) => {
        if (dragging || interacting || !visible || document.hidden) {
          stopAnimation();
          return;
        }

        const progress = Math.min((time - startTime) / MOVE_DURATION, 1);
        track.scrollLeft = start + travel * (1 - Math.pow(1 - progress, 3));
        scheduleSync();

        if (progress < 1) {
          animationFrame = window.requestAnimationFrame(frame);
          return;
        }

        animationFrame = 0;
        track.classList.remove("is-auto-scrolling");
        normalizeLoop();
        onComplete?.();
      };

      animationFrame = window.requestAnimationFrame(frame);
    };

    const playCentered = () => {
      if (!visible || interacting || document.hidden) return;

      normalizeLoop();
      if (!visibleCards.size) updateVisibleFromLayout();
      startVisible();

      const centered = getCentered();
      if (!centered) return;

      if (Math.abs(centerScroll(centered) - track.scrollLeft) > 2) {
        animateTo(centered, playCentered);
        return;
      }

      activate(centered);
    };

    const advance = async () => {
      if (!visible || interacting || document.hidden) return;

      normalizeLoop();
      let centered = activeCard ?? getCentered();
      if (!centered) return;

      if (centered === lastOriginal && lastLeadingClone) {
        await syncVisibleForRebase();
        if (!visible || interacting || document.hidden) return;

        track.classList.add("is-loop-rebasing");
        track.scrollLeft -= setWidth;
        centered = lastLeadingClone;
        activeCard = centered;
        activeVideo = centered.querySelector("video");
      }

      const next = centered.nextElementSibling;
      if (!next?.classList.contains("lc-reel")) {
        track.classList.remove("is-loop-rebasing");
        return;
      }

      setFocus();
      restartOutgoing(centered);
      animateTo(next, () => activate(next));
    };

    function scheduleNext(delay = IDLE_DELAY) {
      window.clearTimeout(autoTimer);
      if (!visible || interacting || document.hidden) return;
      autoTimer = window.setTimeout(advance, delay);
    }

    const resumeAfterInteraction = () => {
      window.clearTimeout(interactionTimer);
      interactionTimer = window.setTimeout(() => {
        if (dragging || !visible) return;
        interacting = false;
        normalizeLoop();
        startVisible();
        playCentered();
      }, IDLE_DELAY);
    };

    const beginInteraction = () => {
      clearTimers();
      stopAnimation();
      setFocus();
      interacting = true;
    };

    /* Três cópias da sequência deixam o loop sem salto visual. */
    const initLoop = () => {
      originals = Array.from(track.querySelectorAll(":scope > .lc-reel"));
      if (!originals.length) return;

      originals.forEach((card, index) => {
        card.dataset.reelIndex = String(index);
      });

      const clone = (card) => {
        const copy = card.cloneNode(true);
        copy.classList.remove("lc-reveal");
        copy.classList.add("lc-reel--clone");
        copy.setAttribute("aria-hidden", "true");
        copy.tabIndex = -1;
        copy.querySelectorAll("a, button").forEach((control) => {
          control.tabIndex = -1;
        });

        const video = copy.querySelector("video");
        if (video) {
          video.removeAttribute("aria-label");
          video.preload = "none";
          video.loop = false;
          video.pause();
        }

        return copy;
      };

      const leading = document.createDocumentFragment();
      const trailing = document.createDocumentFragment();
      originals.forEach((card) => leading.append(clone(card)));
      originals.forEach((card) => trailing.append(clone(card)));
      track.insertBefore(leading, originals[0]);
      track.append(trailing);

      firstOriginal = originals[0];
      firstTrailingClone = track.children[originals.length * 2];
      lastOriginal = originals.at(-1);
      lastLeadingClone = track.children[originals.length - 1];
      allCards = Array.from(track.querySelectorAll(":scope > .lc-reel"));
      videos = allCards.map((card) => card.querySelector("video")).filter(Boolean);

      videos.forEach((video) => {
        video.loop = true;
        video.pause();
        video.addEventListener("ended", () => {
          if (video === activeVideo) scheduleNext(PLAY_DELAY);
        });
      });

      if (hasObserver) {
        cardObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isVisible = entry.isIntersecting && entry.intersectionRect.width > 0;
              if (isVisible) visibleCards.add(entry.target);
              else visibleCards.delete(entry.target);
            });
            scheduleSync();
          },
          { root: track, threshold: 0.01 },
        );
        allCards.forEach((card) => cardObserver.observe(card));
      }

      window.requestAnimationFrame(() => {
        setWidth = firstTrailingClone.offsetLeft - firstOriginal.offsetLeft;
        track.scrollLeft = centerScroll(firstOriginal);
        updateVisibleFromLayout();
        scheduleSync();
      });
    };

    track.addEventListener("scroll", scheduleSync, { passive: true });

    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;

      beginInteraction();
      dragging = true;
      moved = false;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dragStartScroll = track.scrollLeft;
      dragAxis = null;
      track.classList.add("is-dragging");
    });

    track.addEventListener(
      "pointermove",
      (event) => {
        if (!dragging) return;

        const distance = event.clientX - dragStartX;
        const vertical = event.clientY - dragStartY;

        if (!dragAxis && (Math.abs(distance) > 5 || Math.abs(vertical) > 5)) {
          dragAxis = Math.abs(distance) > Math.abs(vertical) ? "x" : "y";
          /* Só captura o ponteiro quando o gesto é horizontal, para o clique no link seguir normal. */
          if (dragAxis === "x") track.setPointerCapture(event.pointerId);
        }

        if (dragAxis !== "x") return;

        if (Math.abs(distance) > 4) moved = true;
        track.scrollLeft = dragStartScroll - distance;
        scheduleSync();
        if (event.cancelable) event.preventDefault();
      },
      { passive: false },
    );

    const finishDrag = (event) => {
      if (!dragging) return;

      dragging = false;
      dragAxis = null;
      track.classList.remove("is-dragging");
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      resumeAfterInteraction();
    };

    track.addEventListener("pointerup", finishDrag);
    track.addEventListener("pointercancel", finishDrag);
    track.addEventListener(
      "wheel",
      () => {
        beginInteraction();
        resumeAfterInteraction();
      },
      { passive: true },
    );

    /* Um arraste não pode virar clique no link do produto. */
    track.addEventListener(
      "click",
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true,
    );

    track.addEventListener("dragstart", (event) => event.preventDefault());

    window.addEventListener(
      "resize",
      () => {
        if (resizeFrame) return;
        resizeFrame = window.requestAnimationFrame(() => {
          resizeFrame = 0;
          if (!firstOriginal || !firstTrailingClone) return;
          setWidth = firstTrailingClone.offsetLeft - firstOriginal.offsetLeft;
          normalizeLoop();
          updateVisibleFromLayout();
          scheduleSync();
          animateTo(getCentered(), playCentered);
        });
      },
      { passive: true },
    );

    initLoop();

    const setTrackVisible = (value) => {
      visible = value;
      clearTimers();
      stopAnimation();

      if (visible) {
        interacting = false;
        scheduleSync();
        autoTimer = window.setTimeout(playCentered, PLAY_DELAY);
      } else {
        pauseAll();
      }
    };

    if (hasObserver) {
      new IntersectionObserver(([entry]) => setTrackVisible(entry.isIntersecting)).observe(track);
    } else {
      setTrackVisible(true);
    }

    onVisibilityChange(() => {
      if (document.hidden) {
        clearTimers();
        stopAnimation();
        pauseAll();
      } else if (visible) {
        scheduleSync();
        autoTimer = window.setTimeout(playCentered, PLAY_DELAY);
      }
    });
  };

  const INITIALIZERS = {
    hero: initHero,
    manifesto: initManifesto,
    reels: initReels,
  };

  const init = (scope = document) => {
    scope.querySelectorAll("[data-lc-section]").forEach((root) => {
      if (root.dataset.lcReady) return;
      root.dataset.lcReady = "true";
      INITIALIZERS[root.dataset.lcSection]?.(root);
      initReveal(root);
    });
  };

  init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
